import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import '../styles/license-management.css';

const LicenseManagement = ({ user, onLicenseUpdate }) => {
  const { t } = useTranslation();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [billingType, setBillingType] = useState('monthly');
  const [processingTrial, setProcessingTrial] = useState(false);
  const [processingSubscription, setProcessingSubscription] = useState(false);

  const plans = {
    starter: {
      name: t('licenseManagement.plans.starter.name'),
      price: { monthly: 29, annual: 290 },
      limits: { maxNodes: 100, maxApiCalls: 1000, maxStorage: 1, maxUsers: 5 },
      features: t('licenseManagement.plans.starter.features', { returnObjects: true })
    },
    professional: {
      name: t('licenseManagement.plans.professional.name'),
      price: { monthly: 99, annual: 990 },
      limits: { maxNodes: 500, maxApiCalls: 5000, maxStorage: 10, maxUsers: 25 },
      features: t('licenseManagement.plans.professional.features', { returnObjects: true })
    },
    enterprise: {
      name: t('licenseManagement.plans.enterprise.name'),
      price: { monthly: 299, annual: 2990 },
      limits: { maxNodes: 1000, maxApiCalls: 10000, maxStorage: 50, maxUsers: 100 },
      features: t('licenseManagement.plans.enterprise.features', { returnObjects: true })
    }
  };

  useEffect(() => {
    loadLicenseInfo();
    checkTrialEligibility();
  }, []);

  const loadLicenseInfo = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/license/my-license');
      setLicenseInfo(response.data);
    } catch (error) {
      console.error('Erreur chargement licence:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkTrialEligibility = async () => {
    try {
      const response = await apiClient.get('/api/license/trial-eligibility');
      setTrialEligibility(response.data);
    } catch (error) {
      console.error('Erreur vérification éligibilité trial:', error);
    }
  };

  const startTrial = async () => {
    try {
      setProcessingTrial(true);
      setError(null);
      
      const response = await apiClient.post('/api/license/start-trial');
      
      if (response.data.success) {
        await loadLicenseInfo();
        if (onLicenseUpdate) onLicenseUpdate();
        toast.success(t('licenseManagement.alerts.trialActivated'));
      }
    } catch (error) {
      console.error('Erreur démarrage trial:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setProcessingTrial(false);
    }
  };

  const subscribe = async () => {
    // Affichage temporaire en attendant l'intégration du paiement
    toast.warn(t('licenseManagement.alerts.featureInDevelopment', { 
      plan: selectedPlan.toUpperCase(), 
      billingType: billingType === 'annual' ? t('licenseManagement.billingTypes.annual') : t('licenseManagement.billingTypes.monthly')
    }));
    
    return; // Arrêter ici pour l'instant
    
    // CODE ORIGINAL (à réactiver plus tard)
    /*
    try {
      setProcessingSubscription(true);
      setError(null);

      // TODO: Intégrer Stripe ici
      const response = await apiClient.post('/api/license/subscribe', {
        plan: selectedPlan,
        type: billingType,
        paymentToken: 'simulation_token' // Remplacer par vrai token Stripe
      });

      if (response.data.success) {
        await loadLicenseInfo();
        toast.success(t('licenseManagement.alerts.subscriptionCreated'));
      }
    } catch (error) {
      console.error('Erreur souscription:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setProcessingSubscription(false);
    }
    */
  };

  const cancelSubscription = async () => {
    if (!window.confirm(t('licenseManagement.confirmations.cancelSubscription'))) {
      return;
    }

    try {
      const response = await apiClient.post('/api/license/cancel-subscription');
      
      if (response.data.success) {
        await loadLicenseInfo();
        toast.success(t('licenseManagement.alerts.subscriptionCanceled'));
      }
    } catch (error) {
      console.error('Erreur annulation:', error);
      setError(error.response?.data?.error || error.message);
    }
  };

  const getUsagePercentage = (current, max) => {
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return '#ef4444';
    if (percentage >= 75) return '#f59e0b';
    return '#10b981';
  };

  if (loading) {
    return (
      <div className="license-management loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('licenseManagement.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="license-management">
      <div className="license-header">
        <h2>{t('licenseManagement.title')}</h2>
        <p>{t('licenseManagement.subtitle')}</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Statut actuel de la licence */}
      {licenseInfo?.hasLicense ? (
        <div className="current-license">
          <div className="license-card">
            <div className="license-status">
              <div className="status-badge status-active">
                {licenseInfo.license.status === 'active' ? t('licenseManagement.currentLicense.statusActive') : t('licenseManagement.currentLicense.statusExpired')}
              </div>
              <div className="license-info">
                <h3>{plans[licenseInfo.license.plan]?.name || licenseInfo.license.plan}</h3>
                <p className="license-type">
                  {licenseInfo.license.type === 'free_trial' ? t('licenseManagement.currentLicense.trialFree') : 
                   licenseInfo.license.type === 'monthly' ? t('licenseManagement.currentLicense.monthly') : 
                   t('licenseManagement.currentLicense.annual')}
                </p>
                <p className="license-expiry">
                  {licenseInfo.license.daysRemaining > 0 
                    ? t('licenseManagement.currentLicense.daysRemaining', { days: licenseInfo.license.daysRemaining })
                    : t('licenseManagement.currentLicense.expired')
                  }
                </p>
              </div>
            </div>

            {licenseInfo.license.type !== 'free_trial' && (
              <button 
                onClick={cancelSubscription}
                className="btn-cancel"
              >
                {t('licenseManagement.currentLicense.cancelSubscription')}
              </button>
            )}
          </div>

          {/* Utilisation actuelle */}
          <div className="usage-section">
            <h4>{t('licenseManagement.usage.title')}</h4>
            <div className="usage-grid">
              {Object.entries(licenseInfo.usage).map(([key, value]) => {
                if (key === 'lastUpdated') return null;
                
                const limit = licenseInfo.limits[`max${key.charAt(0).toUpperCase() + key.slice(1)}`];
                const percentage = getUsagePercentage(value, limit);
                const color = getUsageColor(percentage);
                
                return (
                  <div key={key} className="usage-item">
                    <div className="usage-header">
                      <span className="usage-label">
                        {key === 'nodes' ? `🔗 ${t('licenseManagement.usage.nodes')}` :
                         key === 'apiCalls' ? `🔌 ${t('licenseManagement.usage.apiCalls')}` :
                         key === 'storage' ? `💾 ${t('licenseManagement.usage.storage')} (GB)` :
                         key === 'users' ? `👥 ${t('licenseManagement.usage.users')}` : key}
                      </span>
                      <span className="usage-numbers">
                        {value.toLocaleString()} / {limit.toLocaleString()}
                      </span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className="usage-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: color
                        }}
                      ></div>
                    </div>
                    <span className="usage-percentage" style={{ color }}>
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>

            {licenseInfo.violations?.length > 0 && (
              <div className="violations-alert">
                <h5>⚠️ Limites Dépassées</h5>
                <ul>
                  {licenseInfo.violations.map((violation, index) => (
                    <li key={index}>
                      {violation.type}: {violation.current} / {violation.limit}
                    </li>
                  ))}
                </ul>
                <p>Nous vous recommandons de passer à un plan supérieur.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Pas de licence active */
        <div className="no-license">
          <div className="trial-section">
            {trialEligibility?.eligible ? (
              <div className="trial-offer">
                <h3>🎯 Essai Gratuit Disponible</h3>
                <p>Découvrez toutes les fonctionnalités pendant 30 jours !</p>
                <div className="trial-features">
                  <ul>
                    <li>✅ Accès aux fonctionnalités Starter</li>
                    <li>✅ 50 nœuds</li>
                    <li>✅ 500 appels API</li>
                    <li>✅ 0.5 GB de stockage</li>
                    <li>✅ 5 utilisateurs</li>
                  </ul>
                </div>
                <button 
                  onClick={startTrial}
                  disabled={processingTrial}
                  className="btn-trial"
                >
                  {processingTrial ? t('licenseManagement.buttons.activating') : t('licenseManagement.buttons.startFreeTrial')}
                </button>
              </div>
            ) : (
              <div className="trial-used">
                <h3>❌ Essai gratuit déjà utilisé</h3>
                <p>Vous avez déjà bénéficié de l'essai gratuit. Choisissez un plan pour continuer.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plans disponibles */}
      <div className="plans-section">
        <h3>💎 Plans Disponibles</h3>
        
        <div className="billing-toggle">
          <button 
            className={billingType === 'monthly' ? 'active' : ''}
            onClick={() => setBillingType('monthly')}
          >
            Mensuel
          </button>
          <button 
            className={billingType === 'annual' ? 'active' : ''}
            onClick={() => setBillingType('annual')}
          >
            Annuel
            <span className="discount-badge">-17%</span>
          </button>
        </div>

        <div className="plans-grid">
          {Object.entries(plans).map(([planKey, plan]) => (
            <div 
              key={planKey}
              className={`plan-card ${selectedPlan === planKey ? 'selected' : ''} ${
                licenseInfo?.license?.plan === planKey ? 'current' : ''
              }`}
              onClick={() => setSelectedPlan(planKey)}
            >
              {licenseInfo?.license?.plan === planKey && (
                <div className="current-plan-badge">Plan Actuel</div>
              )}
              
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  <span className="price-amount">
                    {plan.price[billingType]}€
                  </span>
                  <span className="price-period">
                    /{billingType === 'monthly' ? 'mois' : 'an'}
                  </span>
                </div>
                {billingType === 'annual' && (
                  <div className="annual-savings">
                    Économisez {Math.round((1 - plan.price.annual / (plan.price.monthly * 12)) * 100)}%
                  </div>
                )}
              </div>

              <div className="plan-features">
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="feature-check">✅</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {(!licenseInfo?.hasLicense || licenseInfo?.license?.plan !== planKey) && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    subscribe();
                  }}
                  disabled={processingSubscription}
                  className="btn-subscribe"
                >
                  {processingSubscription ? 'Traitement...' : 'Choisir ce plan'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact et support */}
      <div className="support-section">
        <h4>🤝 Besoin d'aide ?</h4>
        <p>
          Notre équipe est là pour vous accompagner. 
          <a href="mailto:nodemented.contact@gmail.com"> Contactez-nous</a> pour toute question.
        </p>
      </div>
    </div>
  );
};

export default LicenseManagement;
